import React from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Badge, Row, Col, Label, Button } from "reactstrap"
import { getKycAssetUrl, getProfileImageUrl } from "helpers/auth_api"

export interface UserDetailsModalUser {
  _id: string
  fullName?: string
  email?: string
  role?: string
  mobileNumber?: string
  state?: string
  city?: string
  address?: string
  aadharNumber?: string
  profileImageUrl?: string
  documents?: { filePath: string; uploadedAt?: string }[]
  kycSelfieDone?: boolean
  kycSignatureDone?: boolean
  kycSubmitted?: boolean
  kycApprovedByAdmin?: boolean
  kycComplete?: boolean
  kycSelfieUrl?: string
  kycSignatureUrl?: string
  kycVideoUrl?: string
  isBlocked?: boolean
}

interface UserDetailsModalProps {
  isOpen: boolean
  toggle: () => void
  user: UserDetailsModalUser | null
  /** When true and user is null, show loading message in modal body */
  loading?: boolean
  /** Admin list: show Approve / Remove approval actions */
  showKycAdminActions?: boolean
  onApproveKyc?: () => void
  onRevokeKyc?: () => void
  kycActionLoading?: boolean
}

function hasAnyKyc(u: UserDetailsModalUser) {
  return !!(u.kycSelfieUrl || u.kycSignatureUrl || u.kycVideoUrl)
}

const UserDetailsModal = ({
  isOpen,
  toggle,
  user,
  loading,
  showKycAdminActions,
  onApproveKyc,
  onRevokeKyc,
  kycActionLoading,
}: UserDetailsModalProps) => {
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered scrollable>
      <ModalHeader toggle={toggle}>
        <span className="d-flex align-items-center gap-2">
          User Details — {user?.fullName || user?.email || "User"}
          {user?.isBlocked && <Badge color="danger">Blocked</Badge>}
        </span>
      </ModalHeader>
      <ModalBody>
        {loading && !user && (
          <div className="text-center py-5 text-muted">
            <div className="spinner-border spinner-border-sm me-2" role="status" />
            Loading user details...
          </div>
        )}
        {!loading && user && (
          <>
            {/* Profile Details */}
            <h6 className="text-primary border-bottom pb-2 mb-3">
              <i className="mdi mdi-account-circle me-1" />
              Profile Details
            </h6>
            <Row className="g-3 mb-4">
              <Col sm={6} md={4}>
                <Label className="small text-muted d-block mb-0">Full Name</Label>
                <span className="fw-medium">{user.fullName || "—"}</span>
              </Col>
              <Col sm={6} md={4}>
                <Label className="small text-muted d-block mb-0">Email</Label>
                <span className="fw-medium">{user.email || "—"}</span>
              </Col>
              <Col sm={6} md={4}>
                <Label className="small text-muted d-block mb-0">Role</Label>
                <span className="fw-medium">{user.role || "—"}</span>
              </Col>
              <Col sm={6} md={4}>
                <Label className="small text-muted d-block mb-0">Mobile</Label>
                <span className="fw-medium">{user.mobileNumber || "—"}</span>
              </Col>
              <Col sm={6} md={4}>
                <Label className="small text-muted d-block mb-0">State</Label>
                <span className="fw-medium">{user.state || "—"}</span>
              </Col>
              <Col sm={6} md={4}>
                <Label className="small text-muted d-block mb-0">City</Label>
                <span className="fw-medium">{user.city || "—"}</span>
              </Col>
              <Col xs={12}>
                <Label className="small text-muted d-block mb-0">Address</Label>
                <span className="fw-medium">{user.address || "—"}</span>
              </Col>
              <Col sm={6} md={4}>
                <Label className="small text-muted d-block mb-0">Aadhar Number</Label>
                <span className="fw-medium">
                  {user.aadharNumber ? "•••• " + user.aadharNumber.slice(-4) : "—"}
                </span>
              </Col>
              {user.profileImageUrl && (
                <Col xs={12}>
                  <Label className="small text-muted d-block mb-1">Profile Photo</Label>
                  <a
                    href={getProfileImageUrl(user.profileImageUrl) || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-inline-block"
                  >
                    <img
                      src={getProfileImageUrl(user.profileImageUrl) || ""}
                      alt="Profile"
                      style={{
                        maxHeight: 120,
                        borderRadius: 8,
                        border: "1px solid #dee2e6",
                      }}
                    />
                  </a>
                </Col>
              )}
              {user.documents && user.documents.length > 0 && (
                <Col xs={12}>
                  <Label className="small text-muted d-block mb-1">Documents</Label>
                  <ul className="list-unstyled mb-0">
                    {user.documents.map((doc, i) => (
                      <li key={i} className="mb-1">
                        <a
                          href={getKycAssetUrl(doc.filePath) || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="mdi mdi-file-document-outline me-1" />
                          Document {i + 1}
                        </a>
                      </li>
                    ))}
                  </ul>
                </Col>
              )}
            </Row>

            {/* KYC Status & Data */}
            <h6 className="text-primary border-bottom pb-2 mb-3">
              <i className="mdi mdi-shield-account me-1" />
              KYC Status & Data
            </h6>
            <div className="mb-3">
              <span className="me-2">Status:</span>
              {(() => {
                const submitted =
                  user.kycSubmitted === true ||
                  !!(user.kycSelfieDone && user.kycSignatureDone)
                if (user.kycComplete) {
                  return <Badge color="success">Approved &amp; verified</Badge>
                }
                if (submitted) {
                  return <Badge color="warning">Pending admin approval</Badge>
                }
                return <Badge color="secondary">KYC not submitted</Badge>
              })()}
              {user.kycSelfieDone && <span className="ms-2 small text-muted">Selfie ✓</span>}
              {user.kycSignatureDone && <span className="ms-1 small text-muted">Signature ✓</span>}
            </div>
            {!hasAnyKyc(user) ? (
              <p className="text-muted mb-0">No KYC data submitted yet.</p>
            ) : (
              <Row className="g-3">
                {user.kycSelfieUrl && (
                  <Col md={4}>
                    <Label className="small fw-semibold text-muted d-block mb-2">Selfie</Label>
                    <a
                      href={getKycAssetUrl(user.kycSelfieUrl) || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="d-inline-block"
                    >
                      <img
                        src={getKycAssetUrl(user.kycSelfieUrl) || ""}
                        alt="KYC Selfie"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 280,
                          objectFit: "contain",
                          borderRadius: 8,
                          border: "1px solid #dee2e6",
                        }}
                      />
                    </a>
                  </Col>
                )}
                {user.kycSignatureUrl && (
                  <Col md={4}>
                    <Label className="small fw-semibold text-muted d-block mb-2">Signature</Label>
                    <a
                      href={getKycAssetUrl(user.kycSignatureUrl) || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="d-inline-block"
                    >
                      <img
                        src={getKycAssetUrl(user.kycSignatureUrl) || ""}
                        alt="KYC Signature"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 280,
                          objectFit: "contain",
                          borderRadius: 8,
                          border: "1px solid #dee2e6",
                          background: "#fff",
                        }}
                      />
                    </a>
                  </Col>
                )}
                {user.kycVideoUrl && (
                  <Col xs={4}>
                    <Label className="small fw-semibold text-muted d-block mb-2">Liveness Video</Label>
                    <video
                      controls
                      style={{
                        maxWidth: "100%",
                        maxHeight: 320,
                        borderRadius: 8,
                        border: "1px solid #dee2e6",
                        background: "#000",
                      }}
                      src={getKycAssetUrl(user.kycVideoUrl) || undefined}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </Col>
                )}
              </Row>
            )}
          </>
        )}
        {!loading && !user && (
          <div className="text-center py-4 text-muted">No user data available.</div>
        )}
      </ModalBody>
      {showKycAdminActions && user && (
        <ModalFooter className="border-top flex-wrap gap-2">
          {(user.kycSubmitted === true || !!(user.kycSelfieDone && user.kycSignatureDone)) &&
            !user.kycComplete && (
              <Button color="success" onClick={onApproveKyc} disabled={kycActionLoading}>
                {kycActionLoading ? "…" : "Approve KYC"}
              </Button>
            )}
          {user.kycComplete && (
            <Button color="warning" outline onClick={onRevokeKyc} disabled={kycActionLoading}>
              {kycActionLoading ? "…" : "Remove approval"}
            </Button>
          )}
        </ModalFooter>
      )}
    </Modal>
  )
}

export default UserDetailsModal
